/**
 * Placa — identifier tag (design-v2).
 *
 * Machine, OP, product code and order numbers, rendered exactly as stored:
 * `ui-monospace` 0.8125rem/500, `plate-bg` (a faint neutral wash) / main text,
 * radius 4, padding 1px 6px, tabular-nums, never wraps or gets translated.
 *
 *   <Placa>MAQ FURACAO 002</Placa>
 *   <Placa title="Ordem de produção">OP-2004</Placa>
 *
 * Plain inline text for assistive tech (no role). Never put machine-status
 * colours on it.
 */
import React from 'react';
import { Box } from '@mui/material';
import { useOctoTokens } from '../theme/octoTokens';
import { octoText } from '../theme/octoStyles';

export interface PlacaProps {
  children: React.ReactNode;
  /** Optional tooltip text (native `title`), e.g. what the identifier is. */
  title?: string;
  className?: string;
  'data-testid'?: string;
}

const Placa: React.FC<PlacaProps> = ({ children, title, className, 'data-testid': testId }) => {
  const t = useOctoTokens();
  return (
    <Box
      component="span"
      translate="no"
      title={title}
      className={className}
      data-testid={testId}
      data-octo="placa"
      sx={{
        ...octoText(t, 'plate'),
        display: 'inline',
        bgcolor: t.color.plateBg,
        borderRadius: `${t.radius.plate}px`,
        padding: '1px 6px',
        whiteSpace: 'nowrap',
        boxDecorationBreak: 'clone',
      }}
    >
      {children}
    </Box>
  );
};

export default Placa;
